import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Modal, message } from 'antd';

import useHeaderTitle from '../../hooks/useHeaderTitle';
import GeneralHelper from '../../helpers/general';
import TimeslotSelector from './components/TimeslotSelector';
import MealSelector from './components/MealSelector';
import DrinkSelector from './components/DrinkSelector';
import OrderConfirmation from './components/OrderConfirmation';

import './BookingPage.less';
import MealCustomization from './components/MealCustomization';
import DrinkCustomization from './components/DrinkCustomization';

const BookingPage = (props) => {
	const [step, setStep] = useState(1);
	const [selectedTimeslot, setSelectedTimeslot] = useState(null);
	const [selectedMeal, setSelectedMeal] = useState(null);
	const [selectedDrink, setSelectedDrink] = useState(null);
	const [orderCutoffModalOpen, setOrderCutoffModalOpen] = useState(false);
	const [orderCutoffMessage, setOrderCutoffMessage] = useState('');

	const [selectedMealOptions, setSelectedMealOptions] = useState([]);
	const [selectedDrinkOptions, setSelectedDrinkOptions] = useState([]);

	const history = useHistory();
	const { location } = history;

	const { getTimeslots, getMeals, getDrinks, createOrder, bookingPageProps } = props;
	const { loadingTimeslots, timeslots, loadingMeals, meals, loadingDrinks, drinks, loadingCreatingOrder, meta } = bookingPageProps;

	useHeaderTitle('金飯碗 Gold Rice Bowl', step > 1);

	// Restore booking state if coming back from Privacy Policy or Terms & Conditions
	useEffect(() => {
		if (location.state?.restoredBookingState) {
			const { step: restoredStep, selectedTimeslot: restoredTimeslot, selectedMeal: restoredMeal, selectedDrink: restoredDrink } = location.state.restoredBookingState;
			setStep(restoredStep);
			setSelectedTimeslot(restoredTimeslot);
			setSelectedMeal(restoredMeal);
			setSelectedDrink(restoredDrink);
		}
	}, [location.state]);

	// Fetch timeslots on component mount
	useEffect(() => {
		getTimeslots();
		getMeals();
		getDrinks();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Override back button behavior
	useEffect(() => {
		const handlePopState = () => {
			if (step > 1) {
				if (!selectedMealOptions.length && step === 4) {
					setStep(step - 2);
				} else if (!selectedDrinkOptions.length && step === 6) {
					setStep(step - 2);
				} else {
					setStep(step - 1);
				}
			}
		};

		if (step > 1) {
			window.history.pushState(null, '', window.location.href);
			window.addEventListener('popstate', handlePopState);
		}

		if (step === 1) {
			setSelectedMeal(null);
			setSelectedDrink(null);
		}

		return () => {
			window.removeEventListener('popstate', handlePopState);
		};
	}, [step]);

	const handleTimeslotContinue = (timeslot) => {
		setSelectedTimeslot(timeslot);
		setStep(2);
	};

	const handleMealContinue = (meal) => {
		if (meal)
			meal.chosen_meal_customizations = null;
		setSelectedMeal(meal);
		setSelectedDrink(null);

		if (meal && meal?.meal_customizations && meal?.meal_customizations.length) {
			setSelectedMealOptions(meal.meal_customizations);
			setStep(3);
		}
		else {
			setSelectedMealOptions([]);
			setStep(4);
		}
	};

	const handleMealCustomizeContinue = (meal) => {
		setSelectedDrink(null);
		setSelectedMeal(meal);

		setStep(4);
	};

	const handleDrinkContinue = (drink) => {
		if (drink)
			drink.chosen_drink_customizations = null;
		setSelectedDrink(drink);

		if (drink && drink?.drink_customizations && drink?.drink_customizations.length) {
			setSelectedDrinkOptions(drink.drink_customizations);
			setStep(5);
		}
		else {
			setSelectedDrinkOptions([]);
			setStep(6);
		}
	};

	const handleDrinkCustomizeContinue = (drink) => {
		setSelectedDrink(drink);
		setStep(6); // Move to confirmation step
	};

	const handleProceedToPayment = (email) => {
		const body = {
			order_date_timeslot_id: selectedTimeslot._id,
			meal_id: selectedMeal.meal_id,
			drink_id: selectedDrink?.drink_id || null,
			price: (selectedMeal.meal_price || 0) + (selectedDrink?.drink_price || 0),
			...selectedMeal.chosen_meal_customizations && { meal_customizations: selectedMeal.chosen_meal_customizations },
			...selectedDrink?.chosen_drink_customizations && { drink_customizations: selectedDrink.chosen_drink_customizations },
			email: email
		};

		createOrder(body);
	};

	const handleSelectAnotherTimeslot = () => {
		setOrderCutoffModalOpen(false);
		setOrderCutoffMessage('');
		setSelectedTimeslot(null);
		setSelectedMeal(null);
		setSelectedDrink(null);
		setStep(1);
		getTimeslots();
		history.push('/booking');
	};

	// Show error message when order creation fails
	useEffect(() => {
		if (meta && meta.code && meta.code !== 200 && !loadingCreatingOrder) {
			const errorMessage = meta.message || 'Failed to create order. Please try again.';
			if (meta.error_code === 'order_cutoff') {
				setOrderCutoffMessage(errorMessage);
				setOrderCutoffModalOpen(true);
				return;
			}
			message.error(errorMessage);
		}
	}, [meta, loadingCreatingOrder]);

	return (
		<div className="booking-container">
			<>
				{step === 1 && (
					<TimeslotSelector
						selectedTimeslot={selectedTimeslot}
						onContinue={handleTimeslotContinue}
						timeslots={timeslots}
						loading={loadingTimeslots}
					/>
				)}

				{step === 2 && (
					<MealSelector
						selectedMeal={selectedMeal}
						onContinue={handleMealContinue}
						onBack={() => {
							setStep(1);
						}}
						meals={meals}
						loading={loadingMeals}
					// onSelectMeal={handleOnSelectMeal}
					/>
				)}

				{step === 3 && (
					<MealCustomization
						selectedMeal={selectedMeal}
						onContinue={handleMealCustomizeContinue}
						onBack={() => setStep(2)}
						loading={loadingMeals}
						customizations={selectedMealOptions}
					/>
				)}

				{step === 4 && (
					<DrinkSelector
						selectedDrink={selectedDrink}
						onContinue={handleDrinkContinue}
						onBack={() => {
							if (selectedMeal?.meal_customizations && selectedMeal?.meal_customizations.length)
								return setStep(3);
							else
								setStep(2);
						}}
						drinks={drinks}
						loading={loadingDrinks}
					// onSelectDrink={handleOnSelectDrink}
					/>
				)}

				{step === 5 && (
					<DrinkCustomization
						selectedDrink={selectedDrink}
						onContinue={handleDrinkCustomizeContinue}
						onBack={() => setStep(6)}
						loading={loadingDrinks}
						customizations={selectedDrinkOptions}
					/>
				)}

				{step === 6 && selectedMeal && (
					<OrderConfirmation
						timeslot={selectedTimeslot}
						order={{ meal: selectedMeal, drink: selectedDrink }}
						onBack={() => {
							if (selectedDrink?.drink_customizations && selectedDrink?.meal_customizations.length)
								return setStep(5);
							else
								setStep(4);
						}}
						onProceedToPayment={handleProceedToPayment}
						loading={loadingCreatingOrder}
						bookingState={{ step, selectedTimeslot, selectedMeal, selectedDrink }}
					/>
				)}
			</>
			<Modal
				open={orderCutoffModalOpen}
				centered
				closable={false}
				maskClosable={false}
				footer={null}
				onCancel={handleSelectAnotherTimeslot}
			>
				<div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
					<h2 style={{ margin: '0 0 8px' }}>Order Time Passed</h2>
					<p style={{ margin: '0 0 16px' }}>{orderCutoffMessage}</p>
					<Button
						type="primary"
						size="large"
						onClick={handleSelectAnotherTimeslot}
						style={{ width: 150 }}
					>
						Okay
					</Button>
				</div>
			</Modal>
		</div>
	);
};

export default BookingPage;
